import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import { 
  LoginRequest, 
  LoginResponse, 
  User, 
  ApiError,
  UpdateUserProfile,
  ChangePasswordRequest,
  MFAMethod,
  MFASetupRequest,
  MFASetupResponse,
  MFAVerifyRequest,
  MFAVerifyResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  RefreshTokenResponse,
  UserListResponse,
  UserFilters,
  CreateUserRequest,
  UpdateUserRequest,
  UserWithProfile,
  Tenant,
  InstitutionListResponse,
  InstitutionFilters,
  InstitutionCreate,
  InstitutionUpdate,
  InstitutionWithDetails,
  Institution,
  Document,
  DocumentCreate,
  DocumentFilters,
  DocumentListResponse,
  DocumentUploadResponse,
  DocumentEncapsulateRequest,
  DocumentEncapsulateResponse,
  Contract,
  ContractCreate,
  ContractUpdate,
  ContractListResponse,
  ContractCloneRequest,
  ContractCloneResponse,
  ContractSegmentPricing,
  ContractSegmentPricingCreate,
  BillingEntryListResponse,
  BillingApprovalRequest,
  BillingApprovalResponse,
  BillingDashboardResponse,
  BillingGenerationRequest,
  BillingGenerationResponse,
  DoctorScoreResponse,
  BillingReportInstitutionResponse,
  BillingReportDoctorResponse,
  BillingReportInstitutionCyclesResponse,
  BillingReportDoctorEntryDetail,
  GuardReportInstitutionRequest,
  GuardReportDoctorRequest,
  GuardReportStandardResponse,
  GuardReportDetailedResponse,
  GuardReportDoctorResponse
} from '../types/api'
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../utils/tokenStorage'

// Configurația de bază pentru API - detecție dinamică
const API_BASE_URL = `${window.location.origin}/api/v1`

// Crearea instanței Axios
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 600 secunde (10 minute) pentru sync și operațiuni mari (243+ studii)
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // CRITICAL: trimite httpOnly cookies în toate request-urile
})

// Interceptor pentru request-uri - adaugă token-ul de autentificare din memoria RAM
apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // IMPORTANT: allow browser to set correct boundary for multipart/form-data
    // Our apiClient has default 'Content-Type: application/json' which breaks FormData uploads.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      try {
        // Axios may normalize headers casing internally
        delete (config.headers as any)['Content-Type']
        delete (config.headers as any)['content-type']
      } catch {
        // ignore
      }
    }
    // console.log('API Request:', config.url, 'Token present:', !!token)
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor pentru response-uri - gestionează erorile de autentificare
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError<ApiError>) => {
    // console.log('API Error:', error.response?.status, error.config?.url)
    const originalRequest = error.config

    // Dacă primim 401 și nu am încercat deja să refresh token-ul
    if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
      (originalRequest as any)._retry = true

      try {
        const refreshToken = getRefreshToken()
        // Prefer silent-refresh (httpOnly cookie) when refresh token is cookie-managed
        // or not a JWT (prevents "Not enough segments" errors).
        const canUseJwtRefresh = !!refreshToken && refreshToken.includes('.')

        if (canUseJwtRefresh) {
          // console.log('Attempting token refresh...')
          const response = await axios.post<RefreshTokenResponse>(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken
          })

          const { access_token } = response.data
          setTokens(access_token, refreshToken)

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`
          }
          return axios(originalRequest as any)
        }

        // Cookie-based session (recommended)
        // console.log('Attempting silent refresh via httpOnly cookie...')
        const silent = await axios.post<RefreshTokenResponse>(
          `${API_BASE_URL}/auth/silent-refresh`,
          {},
          { withCredentials: true }
        )

        const { access_token } = silent.data
        if (access_token) {
          setTokens(access_token, 'cookie-managed')
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`
          }
          return axios(originalRequest as any)
        }
      } catch (refreshError) {
        console.error('Eroare la refresh token:', refreshError)
        // Dacă refresh-ul eșuează, șterge token-urile din memoria RAM
        clearTokens()
        
        // ⚠️ CRITICAL: NU redirecta dacă suntem deja pe pagina de login
        // Altfel creăm un infinite loop (checkAuth → silentRefresh → 401 → redirect → checkAuth)
        if (!window.location.pathname.startsWith('/login') && 
            !window.location.pathname.startsWith('/forgot-password') &&
            !window.location.pathname.startsWith('/reset-password')) {
          window.location.href = '/login'
        }
      }
    }

    return Promise.reject(error)
  }
)

// Serviciul de autentificare
export const authService = {
  // Login
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials)
    return response.data
  },

  // Logout
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      console.error('Eroare la logout:', error)
    } finally {
      // Șterge token-urile din memoria RAM indiferent de rezultat
      clearTokens()
    }
  },

  // Obține datele utilizatorului curent
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/auth/me')
    return response.data
  },

  // Refresh token
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh', {
      refresh_token: refreshToken
    })
    return response.data
  },

  // Silent refresh - folosit la page load pentru a reîncărca sesiunea automat
  // Verifică httpOnly cookie cu refresh_token și returnează access_token nou
  async silentRefresh(): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>('/auth/silent-refresh', {})
    return response.data
  },

  // MFA - Obține metodele MFA ale utilizatorului
  async getMFAMethods(): Promise<MFAMethod[]> {
    const response = await apiClient.get<MFAMethod[]>('/auth/mfa/methods')
    return response.data
  },

  // MFA - Configurează o metodă MFA
  async setupMFA(setupData: MFASetupRequest): Promise<MFASetupResponse> {
    const response = await apiClient.post<MFASetupResponse>('/auth/mfa/setup', setupData)
    return response.data
  },

  // MFA - Verifică codul MFA
  async verifyMFA(verifyData: MFAVerifyRequest): Promise<MFAVerifyResponse> {
    const response = await apiClient.post<MFAVerifyResponse>('/auth/mfa/verify', verifyData)
    return response.data
  },

  // MFA - Dezactivează o metodă MFA
  async disableMFA(methodId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/auth/mfa/methods/${methodId}`)
    return response.data
  },

  // MFA - Obține status MFA utilizator
  async getMFAStatus(): Promise<any> {
    const response = await apiClient.get('/mfa/status')
    return response.data
  },

  // MFA - Trimite cod MFA pentru o metodă
  async sendMFACode(methodId: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(`/mfa/methods/${methodId}/send-code`)
    return response.data
  },

  // MFA - List trusted devices
  async listTrustedDevices(): Promise<any[]> {
    const response = await apiClient.get('/mfa/trusted-devices')
    return response.data
  },

  // MFA - Revoke trusted device
  async revokeTrustedDevice(deviceId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/mfa/trusted-devices/${deviceId}`)
    return response.data
  },

  // Reset parolă - Trimite email pentru reset
  async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    const response = await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', data)
    return response.data
  },

  // Reset parolă - Resetează parola cu token
  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await apiClient.post<ResetPasswordResponse>('/auth/reset-password', data)
    return response.data
  }
}

// Serviciul pentru user management
export const userService = {
  // Obține lista utilizatorilor cu filtrare și paginare
  async getUsers(filters: UserFilters = {}): Promise<UserListResponse> {
    const params = new URLSearchParams()
    
    if (filters.role) params.append('role', filters.role)
    if (filters.institution_id) params.append('institution_id', filters.institution_id)
    if (filters.q) params.append('q', filters.q)
    if (filters.active !== undefined) params.append('active', filters.active.toString())
    if (filters.skip) params.append('skip', filters.skip.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())

    const response = await apiClient.get<User[]>(`/users?${params.toString()}`)
    // API-ul returnează array direct, convertim la UserListResponse
    const users = response.data
    return {
      users: users,
      total: users.length,
      page: filters.skip ? Math.floor(filters.skip / (filters.limit || 10)) : 0,
      limit: filters.limit || 10
    }
  },

  // Obține un utilizator specific cu profil
  async getUser(userId: string): Promise<UserWithProfile> {
    const response = await apiClient.get<UserWithProfile>(`/users/${userId}`)
    return response.data
  },

  // Creează un utilizator nou
  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await apiClient.post<User>('/users', userData)
    return response.data
  },

  // Actualizează un utilizator
  async updateUser(userId: string, userData: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<User>(`/users/${userId}`, userData)
    return response.data
  },

  // Șterge un utilizator (soft delete)
  async deleteUser(userId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/users/${userId}`)
    return response.data
  },

  // Obține profilul utilizatorului
  async getUserProfile(userId: string): Promise<any> {
    const response = await apiClient.get<any>(`/users/${userId}/profile`)
    return response.data
  },

  // Actualizează profilul utilizatorului
  async updateUserProfile(userId: string, profileData: UpdateUserProfile): Promise<any> {
    const response = await apiClient.put<any>(`/users/${userId}/profile`, profileData)
    return response.data
  },

  // Schimbă parola utilizatorului
  async changePassword(userId: string, passwordData: ChangePasswordRequest): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(`/users/${userId}/change-password`, passwordData)
    return response.data
  },

  // Activează/dezactivează un utilizator
  async updateUserStatus(userId: string, active: boolean): Promise<{ message: string }> {
    const response = await apiClient.patch<{ message: string }>(`/users/${userId}/status`, { active })
    return response.data
  },

  // Obține scorul utilizatorului
  async getUserScore(userId: string, fromDate?: string, toDate?: string): Promise<any> {
    const params = new URLSearchParams()
    if (fromDate) params.append('from_date', fromDate)
    if (toDate) params.append('to_date', toDate)

    const response = await apiClient.get<any>(`/users/${userId}/score?${params.toString()}`)
    return response.data
  },

  // Actualizează modalitățile unui medic
  async updateMedicModalities(userId: string, modalities: string[]): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>(`/users/${userId}/modalities`, { modalities })
    return response.data
  },

  // Obține tenant-urile disponibile pentru crearea utilizatorilor
  async getAvailableTenants(): Promise<Tenant[]> {
    const response = await apiClient.get<Tenant[]>('/users/list-tenants')
    return response.data
  },

  // Obține instituțiile pentru un tenant
  async getInstitutionsForTenant(tenantId: string): Promise<Institution[]> {
    const response = await apiClient.get<Institution[]>(`/tenants/institutions?tenant_id=${tenantId}`)
    return response.data
  },

  // Obține instituțiile unui utilizator
  async getUserInstitutions(userId: string): Promise<Institution[]> {
    const response = await apiClient.get<Institution[]>(`/users/${userId}/institutions`)
    return response.data
  },

  // Actualizează instituțiile unui utilizator
  async updateUserInstitutions(userId: string, institutionIds: string[]): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>(`/users/${userId}/institutions`, { 
      institution_ids: institutionIds 
    })
    return response.data
  },

  // Admin: Reset parolă utilizator
  async adminResetPassword(
    userId: string, 
    data: { auto_generate: boolean; new_password?: string }
  ): Promise<{ message: string; new_password: string; user_email: string; user_id: string }> {
    const response = await apiClient.post<{ message: string; new_password: string; user_email: string; user_id: string }>(
      `/users/${userId}/admin-reset-password`, 
      data
    )
    return response.data
  }
}

// Serviciul pentru instituții medicale
export const institutionService = {
  // Obține lista instituțiilor cu filtrare și paginare
  async getInstitutions(filters: InstitutionFilters = {}): Promise<InstitutionListResponse> {
    const params = new URLSearchParams()
    
    if (filters.active !== undefined) params.append('active', filters.active.toString())
    if (filters.pacs_id) params.append('pacs_id', filters.pacs_id)
    if (filters.q) params.append('q', filters.q)
    if (filters.skip) params.append('skip', filters.skip.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())

    const response = await apiClient.get<InstitutionWithDetails[]>(`/tenants/institutions?${params.toString()}`)
    // API-ul returnează array direct, convertim la InstitutionListResponse
    const institutions = response.data
    return {
      institutions: institutions,
      total: institutions.length,
      page: filters.skip ? Math.floor(filters.skip / (filters.limit || 10)) : 0,
      limit: filters.limit || 10
    }
  },

  // Obține o instituție specifică
  async getInstitution(institutionId: string): Promise<InstitutionWithDetails> {
    const response = await apiClient.get<InstitutionWithDetails>(`/tenants/institutions/${institutionId}`)
    return response.data
  },

  // Creează o instituție nouă
  async createInstitution(institutionData: InstitutionCreate): Promise<InstitutionWithDetails> {
    const response = await apiClient.post<InstitutionWithDetails>('/tenants/institutions', institutionData)
    return response.data
  },

  // Actualizează o instituție
  async updateInstitution(institutionId: string, institutionData: InstitutionUpdate): Promise<InstitutionWithDetails> {
    const response = await apiClient.put<InstitutionWithDetails>(`/tenants/institutions/${institutionId}`, institutionData)
    return response.data
  },

  // Șterge o instituție
  async deleteInstitution(institutionId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/tenants/institutions/${institutionId}`)
    return response.data
  },

           // Activează/dezactivează o instituție
         async updateInstitutionStatus(institutionId: string, active: boolean): Promise<{ message: string }> {
           const response = await apiClient.patch<{ message: string }>(`/tenants/institutions/${institutionId}`, { active })
           return response.data
         },

         // Obține statistici pentru o instituție
         async getInstitutionStats(institutionId: string): Promise<any> {
           const response = await apiClient.get<any>(`/tenants/institutions/${institutionId}/stats`)
           return response.data
         },

         // Obține membrii unei instituții
         async getInstitutionMembers(institutionId: string): Promise<any[]> {
           const response = await apiClient.get<any[]>(`/tenants/institutions/${institutionId}/members`)
           return response.data
         },

         // Adaugă membru la instituție
         async addInstitutionMember(institutionId: string, userId: string, role: string): Promise<any> {
           const response = await apiClient.post<any>(`/tenants/institutions/${institutionId}/members`, {
             user_id: userId,
             role: role
           })
           return response.data
         },

         // Elimină membru din instituție
         async removeInstitutionMember(institutionId: string, userId: string): Promise<{ message: string }> {
           const response = await apiClient.delete<{ message: string }>(`/tenants/institutions/${institutionId}/members/${userId}`)
           return response.data
         },

         // ✨ Adaugă coordonator pentru instituție (medic care vede toate studiile)
         async addInstitutionCoordinator(institutionId: string, userId: string): Promise<any> {
           // Obține lista curentă de instituții coordonate
           const currentResponse = await apiClient.get<any>(`/users/${userId}/coordinated-institutions`)
           const currentInstitutionIds = (currentResponse.data || []).map((i: any) => i.id)
           
           // Adaugă noua instituție dacă nu există deja
           if (!currentInstitutionIds.includes(institutionId)) {
             currentInstitutionIds.push(institutionId)
           }
           
           // Actualizează lista
           const response = await apiClient.put<any>(`/users/${userId}/coordinated-institutions`, {
             institution_ids: currentInstitutionIds
           })
           return response.data
         },

         // ✨ Elimină coordonator pentru instituție
         async removeInstitutionCoordinator(institutionId: string, userId: string): Promise<any> {
           // Obține lista curentă de instituții coordonate
           const currentResponse = await apiClient.get<any>(`/users/${userId}/coordinated-institutions`)
           const currentInstitutionIds = (currentResponse.data || [])
             .map((i: any) => i.id)
             .filter((id: string) => id !== institutionId)
           
           // Actualizează lista fără instituția curentă
           const response = await apiClient.put<any>(`/users/${userId}/coordinated-institutions`, {
             institution_ids: currentInstitutionIds
           })
           return response.data
         },

         // ✨ Obține instituțiile coordonate pentru un utilizator
         async getUserCoordinatedInstitutions(userId: string): Promise<Array<{id: string, name: string}>> {
           const response = await apiClient.get<any>(`/users/${userId}/coordinated-institutions`)
           return response.data || []
         }
}

// Servicii pentru documente
export const documentsApi = {
  // Obține lista documentelor cu filtrare
  getDocuments: async (filters: DocumentFilters = {}): Promise<DocumentListResponse> => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v))
        } else {
          params.append(key, String(value))
        }
      }
    })
    
    const response = await apiClient.get<DocumentListResponse>(`/documents?${params.toString()}`)
    return response.data
  },

  // Obține un document specific
  getDocument: async (id: string): Promise<Document> => {
    const response = await apiClient.get<Document>(`/documents/${id}`)
    return response.data
  },

  // Upload document
  uploadDocument: async (
    file: File, 
    data: DocumentCreate
  ): Promise<DocumentUploadResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('kind', data.kind)
    
    if (data.patient_id) {
      formData.append('patient_id', data.patient_id)
    }

    if (data.tags && data.tags.length > 0) {
      formData.append('tags', JSON.stringify(data.tags))
    }

    // ✨ ETAPA 4: Câmpuri noi pentru metadata
    if (data.document_date) {
      formData.append('document_date', data.document_date)
    }

    if (data.source_institution) {
      formData.append('source_institution', data.source_institution)
    }

    if (data.notes) {
      formData.append('notes', data.notes)
    }

    const response = await apiClient.post<DocumentUploadResponse>('/documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Download document
  downloadDocument: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/documents/${id}/download`, {
      responseType: 'blob',
    })
    return response.data
  },

  // Șterge document (soft delete)
  deleteDocument: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(`/documents/${id}`)
    return response.data
  },

  // Update document (ETAPA 4)
  updateDocument: async (id: string, data: Partial<Document>): Promise<Document> => {
    const response = await apiClient.put<Document>(`/documents/${id}`, data)
    return response.data
  },

  // Restore document
  restoreDocument: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/documents/${id}/restore`)
    return response.data
  },

  // Upload DICOM extern (ETAPA 2)
  uploadExternalDicom: async (
    patientId: string,
    files: File[],
    institutionId: string,
    sourceInstitution?: string,
    notes?: string,
    cnpMismatchConfirmed?: boolean,
    confirmationReason?: string
  ): Promise<any> => {
    const formData = new FormData()
    
    // Adaugă toate fișierele
    files.forEach(file => {
      formData.append('files', file)
    })
    
    formData.append('institution_id', institutionId)
    
    if (sourceInstitution) {
      formData.append('source_institution', sourceInstitution)
    }
    
    if (notes) {
      formData.append('notes', notes)
    }
    
    if (cnpMismatchConfirmed) {
      formData.append('cnp_mismatch_confirmed', 'true')
    }
    
    if (confirmationReason) {
      formData.append('confirmation_reason', confirmationReason)
    }
    
    const response = await apiClient.post(
      `/patients/${patientId}/dicom/upload-external`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 900000, // 15 minutes timeout pentru upload mare (ZIP, conexiuni slabe)
      }
    )
    return response.data
  },

  // Upload DICOM extern cu creare automată pacient
  uploadExternalDicomAuto: async (
    files: File[],
    institutionId: string,
    sourceInstitution?: string,
    notes?: string
  ): Promise<any> => {
    const formData = new FormData()
    
    // Adaugă toate fișierele
    files.forEach(file => {
      formData.append('files', file)
    })
    
    formData.append('institution_id', institutionId)
    
    if (sourceInstitution) {
      formData.append('source_institution', sourceInstitution)
    }
    
    if (notes) {
      formData.append('notes', notes)
    }
    
    const response = await apiClient.post(
      `/patients/dicom/upload-external-auto`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 900000, // 15 minutes timeout pentru upload mare
      }
    )
    return response.data
  },

  // Encapsulează document în DICOM
  encapsulateDocument: async (
    id: string, 
    data: DocumentEncapsulateRequest
  ): Promise<DocumentEncapsulateResponse> => {
    const response = await apiClient.post<DocumentEncapsulateResponse>(
      `/documents/${id}/encapsulate-dicom`,
      data
    )
    return response.data
  },

  // Obține documentele unui pacient
  getPatientDocuments: async (
    patientId: string, 
    filters: Omit<DocumentFilters, 'patient_id'> = {}
  ): Promise<DocumentListResponse> => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v))
        } else {
          params.append(key, String(value))
        }
      }
    })
    
    const response = await apiClient.get<DocumentListResponse>(
      `/documents/patient/${patientId}?${params.toString()}`
    )
    return response.data
  },

  // Obține documentele unui studiu
  getStudyDocuments: async (
    studyId: string, 
    filters: Omit<DocumentFilters, 'study_id'> = {}
  ): Promise<DocumentListResponse> => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v))
        } else {
          params.append(key, String(value))
        }
      }
    })
    
    const response = await apiClient.get<DocumentListResponse>(
      `/documents/study/${studyId}?${params.toString()}`
    )
    return response.data
  },

  // Funcții pentru pacienți
  getPatients: async (
    page: number = 1,
    size: number = 20,
    search?: string,
    assignedToMedic?: string
  ): Promise<{
    patients: Array<{
      id: string;
      first_name: string;
      last_name: string;
      cnp: string | null;
      dob: string | null;
      sex: string | null;
      phone: string | null;
      email: string | null;
      tenant_id: string;
      institution_id: string;
      created_at: string;
      updated_at: string;
      deleted_at: string | null;
    }>;
    total: number;
    page: number;
    size: number;
    pages: number;
  }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    })
    
    if (search) {
      params.append('q', search)
    }
    
    if (assignedToMedic) {
      params.append('assigned_to_medic', assignedToMedic)
    }
    
    const response = await apiClient.get(`/patients/?${params.toString()}`)
    return response.data
  },

  updatePatient: async (
    patientId: string,
    patientData: {
      first_name?: string;
      last_name?: string;
      cnp?: string;
      dob?: string;
      sex?: string;
      phone?: string;
      email?: string;
    }
  ): Promise<{
    id: string;
    first_name: string;
    last_name: string;
    cnp: string | null;
    dob: string | null;
    sex: string | null;
    phone: string | null;
    email: string | null;
    tenant_id: string;
    institution_id: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  }> => {
    const response = await apiClient.put(`/patients/${patientId}`, patientData)
    return response.data
  },

  patchPatient: async (
    patientId: string,
    patientData: {
      first_name?: string;
      last_name?: string;
      cnp?: string;
      dob?: string;
      sex?: string;
      phone?: string;
      email?: string;
    }
  ): Promise<{
    id: string;
    first_name: string;
    last_name: string;
    cnp: string | null;
    dob: string | null;
    sex: string | null;
    phone: string | null;
    email: string | null;
    tenant_id: string;
    institution_id: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  }> => {
    const response = await apiClient.patch(`/patients/${patientId}`, patientData)
    return response.data
  },
}

export default apiClient

export const medicApi = {
  // Funcții pentru dashboard-ul medicului
  getDoctorDashboard: async (): Promise<{
    stats: {
      assignedStudies: number;
      completedToday: number;
      pendingReports: number;
      urgentStudies: number;
      averageTime: number;
      score: number;
    };
    assignedStudies: Array<{
      id: string;
      patientName: string;
      studyType: string;
      priority: string;
      assignedAt: string;
      status: string;
      study_uid: string;
      accession_number: string;
      description: string;
      institution_name?: string;
    }>;
    recentPatients: Array<{
      id: string;
      first_name: string;
      last_name: string;
      cnp: string | null;
      dob: string | null;
      sex: string | null;
      phone: string | null;
      email: string | null;
      studies_count?: number;
      last_study_date?: string;
    }>;
    shifts: Array<{
      id: string;
      start_at: string;
      end_at: string;
      modalities: string[];
      institution_name: string;
      notes?: string;
    }>;
    performance: {
      studiesThisWeek: number;
      studiesPlanned: number;
      urgentStudies: number;
      draftReports: number;
    };
  }> => {
    const response = await apiClient.get('/doctor/dashboard')
    return response.data
  },

  // Funcții pentru studii
  getStudies: async (
    page: number = 1,
    size: number = 20,
    filters: {
      patient_id?: string;
      status?: string;
      urgency?: string;
      modality?: string;
      doctor_id?: string;
      institution_id?: string;
      from?: string;
      to?: string;
      q?: string;
      assignedTo?: string;
    } = {}
  ): Promise<{
    studies: Array<{
      id: string;
      study_uid: string;
      accession_number: string;
      study_date: string;
      description: string;
      priority: string;
      status: string;
      tenant_id: string;
      pacs_id: string;
      institution_id: string;
      patient_id: string;
      assigned_to: string | null;
      assigned_at: string | null;
      pickup_at: string | null;
      first_edit_at: string | null;
      finalized_at: string | null;
      auto_expire_at: string | null;
      created_at: string;
      updated_at: string;
      patient?: {
        first_name: string;
        last_name: string;
        cnp: string;
      };
    }>;
    total: number;
    page: number;
    size: number;
    pages: number;
  }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    })
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })
    
    const response = await apiClient.get(`/studies?${params.toString()}`)
    return response.data
  },

  // Hard delete studiu (DOAR pentru admin)
  hardDeleteStudy: async (studyId: string, reason: string): Promise<{
    success: boolean;
    study_id: string;
    deleted_entities: Record<string, number>;
    warnings: string[];
    deleted_at: string;
  }> => {
    const response = await apiClient.delete(`/studies/${studyId}/hard-delete`, {
      data: {
        reason: reason,
        confirm_deletion: true
      }
    })
    return response.data
  },

  // Obține datele de doză de radiație (RDSR) pentru un studiu
  getRadiationDose: async (studyId: string): Promise<{
    has_rdsr: boolean;
    modality_type: string | null;
    total_dap: number | null;
    total_dap_unit: string;
    total_air_kerma: number | null;
    total_dlp: number | null;
    total_dlp_unit: string;
    events: Array<{
      event_number: number;
      description: string | null;
      dap: number | null;
      dap_unit: string;
      air_kerma: number | null;
      dlp: number | null;
      dlp_unit: string;
      ctdi_vol: number | null;
      kvp: number | null;
      mas: number | null;
      exposure_time: number | null;
    }>;
    events_count: number;
    rdsr_series_uid: string | null;
    rdsr_instance_uid: string | null;
    device_name: string | null;
    manufacturer: string | null;
  }> => {
    const response = await apiClient.get(`/studies/${studyId}/radiation-dose`)
    return response.data
  },

  // Funcții pentru rapoarte
  getReports: async (
    page: number = 1,
    size: number = 20,
    filters: {
      studyId?: string;
      authorId?: string;
      status?: string;
    } = {}
  ): Promise<{
    reports: Array<{
      id: string;
      study_id: string;
      author_id: string;
      status: string;
      html: string;
      editor_html: string;
      editor_json: any;
      pdf_url: string | null;
      version: number;
      signed_at: string | null;
      finalized_at: string | null;
      created_at: string;
      updated_at: string;
      study?: {
        description: string;
        patient?: {
          first_name: string;
          last_name: string;
        };
      };
    }>;
    total: number;
    page: number;
    size: number;
    pages: number;
  }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    })
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })
    
    const response = await apiClient.get(`/reports?${params.toString()}`)
    return response.data
  },

  // Funcții pentru gărzi
  getShifts: async (
    page: number = 1,
    size: number = 20,
    filters: {
      institutionId?: string;
      doctor_id?: string;
      from?: string;
      to?: string;
      modalities?: string[];
    } = {}
  ): Promise<{
    shifts: Array<{
      id: string;
      tenant_id: string;
      institution_id: string;
      doctor_id: string;
      start_at: string;
      end_at: string;
      modalities: string[];
      auto_assign: boolean;
      notes: string | null;
      created_at: string;
      institution?: {
        name: string;
      };
    }>;
    total: number;
    page: number;
    size: number;
    pages: number;
  }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    })
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v))
        } else {
          params.append(key, String(value))
        }
      }
    })
    
    const response = await apiClient.get(`/shifts?${params.toString()}`)
    return response.data
  },

  // Funcții pentru scoring
  getMedicScore: async (doctorId: string): Promise<DoctorScoreResponse> => {
    const response = await apiClient.get(`/scoring/doctor/${doctorId}`)
    return response.data
  },

  // Funcții pentru KPI-uri
  getKPIs: async (): Promise<{
    studies: {
      total: number;
      completed: number;
      in_progress: number;
      overdue: number;
    };
    patients: {
      total: number;
      active: number;
      new_this_month: number;
    };
    reports: {
      total: number;
      finalized: number;
      draft: number;
    };
    documents: {
      total: number;
      dicom: number;
      non_dicom: number;
    };
    performance: {
      avg_time: number;
      sla_compliance: number;
    };
    users: {
      total: number;
      medics: number;
      operators: number;
    };
    shifts: {
      total: number;
      active: number;
      completed: number;
    };
  }> => {
    const response = await apiClient.get('/kpi/summary')
    return response.data
  },
}

// Admin API functions
export const adminApi = {
  // Funcția pentru obținerea setărilor aplicației
  getAppSettings: async (): Promise<{
    app_name: string
    app_description: string
    app_version: string
    app_company: string
    app_website?: string
    app_support_email?: string
    app_logo_url?: string
    app_favicon_url?: string
    app_theme_primary_color: string
    app_theme_secondary_color: string
    app_theme_mode: 'light' | 'dark' | 'auto'
    app_language: string
    app_timezone: string
    app_date_format: string
    app_time_format: string
    app_currency: string
    app_currency_symbol: string
  }> => {
    const response = await apiClient.get('/admin/app-settings')
    return response.data
  },

  // Funcția pentru obținerea setărilor sistemului
  getSystemSettings: async (): Promise<any> => {
    const response = await apiClient.get('/admin/settings')
    return response.data
  },

  // Funcția pentru actualizarea setărilor sistemului
  updateSystemSettings: async (settings: any): Promise<any> => {
    const response = await apiClient.patch('/admin/settings', settings)
    return response.data
  },
}

// Serviciul pentru PACS
export const pacsService = {
  // Obține lista PACS-urilor
  async getPacs(): Promise<any[]> {
    const response = await apiClient.get('/pacs/')
    return response.data
  },

  // Obține studiile din PACS
  async getPacsStudies(pacsId?: string, limit: number = 100): Promise<any> {
    const params = new URLSearchParams()
    if (pacsId) params.append('pacs_id', pacsId)
    params.append('limit', limit.toString())
    
    const response = await apiClient.get(`/pacs/studies?${params.toString()}`)
    return response.data
  },

  // Obține un PACS specific
  async getPacsById(pacsId: string): Promise<any> {
    const response = await apiClient.get(`/pacs/${pacsId}`)
    return response.data
  },
}

// =============================================================================
// NEW CLEAN BILLING API
// =============================================================================

export const billingApi = {
  // Contract management
  async getContracts(filters?: {
    doctor_id?: string
    institution_id?: string
    contract_type?: 'per_hour' | 'per_segment'
    is_active?: boolean
    page?: number
    size?: number
  }): Promise<ContractListResponse> {
    const params = new URLSearchParams()
    if (filters?.doctor_id) params.append('doctor_id', filters.doctor_id)
    if (filters?.institution_id) params.append('institution_id', filters.institution_id)
    if (filters?.contract_type) params.append('contract_type', filters.contract_type)
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString())
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.size) params.append('size', filters.size.toString())
    
    const response = await apiClient.get<ContractListResponse>(`/billing/contracts?${params.toString()}`)
    return response.data
  },

  async createContract(contractData: ContractCreate): Promise<Contract> {
    const response = await apiClient.post<Contract>('/billing/contracts', contractData)
    return response.data
  },

  async getContract(contractId: string): Promise<Contract> {
    const response = await apiClient.get<Contract>(`/billing/contracts/${contractId}`)
    return response.data
  },

  async updateContract(contractId: string, contractData: ContractUpdate): Promise<Contract> {
    const response = await apiClient.put<Contract>(`/billing/contracts/${contractId}`, contractData)
    return response.data
  },

  async deleteContract(contractId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/billing/contracts/${contractId}`)
    return response.data
  },

  async cloneContract(contractId: string, cloneData: ContractCloneRequest): Promise<ContractCloneResponse> {
    const response = await apiClient.post<ContractCloneResponse>(`/billing/contracts/${contractId}/clone`, cloneData)
    return response.data
  },

  // Contract segment pricing
  async getContractSegmentPricing(contractId: string): Promise<ContractSegmentPricing[]> {
    const response = await apiClient.get<ContractSegmentPricing[]>(`/billing/contracts/${contractId}/segment-pricing`)
    return response.data
  },

  async createContractSegmentPricing(contractId: string, pricingData: ContractSegmentPricingCreate): Promise<ContractSegmentPricing> {
    const response = await apiClient.post<ContractSegmentPricing>(`/billing/contracts/${contractId}/segment-pricing`, pricingData)
    return response.data
  },

  async updateContractSegmentPricing(contractId: string, pricingId: string, pricingData: Partial<ContractSegmentPricingCreate>): Promise<ContractSegmentPricing> {
    const response = await apiClient.put<ContractSegmentPricing>(`/billing/contracts/${contractId}/segment-pricing/${pricingId}`, pricingData)
    return response.data
  },

  async deleteContractSegmentPricing(contractId: string, pricingId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/billing/contracts/${contractId}/segment-pricing/${pricingId}`)
    return response.data
  },

  // Billing entries
  async getBillingEntries(filters?: {
    doctor_id?: string
    institution_id?: string
    billing_type?: 'segment' | 'hour'
    status?: 'draft' | 'approved' | 'rejected' | 'paid'
    from_date?: string
    to_date?: string
    page?: number
    size?: number
  }): Promise<BillingEntryListResponse> {
    const params = new URLSearchParams()
    if (filters?.doctor_id) params.append('doctor_id', filters.doctor_id)
    if (filters?.institution_id) params.append('institution_id', filters.institution_id)
    if (filters?.billing_type) params.append('billing_type', filters.billing_type)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.from_date) params.append('from_date', filters.from_date)
    if (filters?.to_date) params.append('to_date', filters.to_date)
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.size) params.append('size', filters.size.toString())
    
    const response = await apiClient.get<BillingEntryListResponse>(`/billing/entries?${params.toString()}`)
    return response.data
  },

  async approveBillingEntry(entryId: string, approvalData: BillingApprovalRequest): Promise<BillingApprovalResponse> {
    const response = await apiClient.post<BillingApprovalResponse>(`/billing/entries/${entryId}/approve`, approvalData)
    return response.data
  },

  // Dashboard and generation
  async getBillingDashboard(filters?: {
    from_date?: string
    to_date?: string
    doctor_id?: string
    institution_id?: string
    page?: number
    size?: number
  }): Promise<BillingDashboardResponse> {
    const params = new URLSearchParams()
    if (filters?.from_date) params.append('from_date', filters.from_date)
    if (filters?.to_date) params.append('to_date', filters.to_date)
    if (filters?.doctor_id) params.append('doctor_id', filters.doctor_id)
    if (filters?.institution_id) params.append('institution_id', filters.institution_id)
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.size) params.append('size', filters.size.toString())
    
    const response = await apiClient.get<BillingDashboardResponse>(`/billing/dashboard?${params.toString()}`)
    return response.data
  },

  async generateBilling(generationData: BillingGenerationRequest): Promise<BillingGenerationResponse> {
    const response = await apiClient.post<BillingGenerationResponse>('/billing/generate', generationData)
    return response.data
  },

  async getBillingReportsInstitutions(params: {
    from_date?: string
    to_date?: string
    institution_id?: string
    status?: string
  } = {}): Promise<BillingReportInstitutionResponse> {
    const searchParams = new URLSearchParams()
    if (params.from_date) searchParams.append('from_date', params.from_date)
    if (params.to_date) searchParams.append('to_date', params.to_date)
    if (params.institution_id) searchParams.append('institution_id', params.institution_id)
    if (params.status) searchParams.append('status', params.status)

    const response = await apiClient.get<BillingReportInstitutionResponse>(`/billing/reports/institutions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`)
    return response.data
  },

  async getBillingInstitutionCycles(institutionId: string, params: { year?: number } = {}): Promise<BillingReportInstitutionCyclesResponse> {
    const searchParams = new URLSearchParams()
    if (params.year) searchParams.append('year', params.year.toString())

    const response = await apiClient.get<BillingReportInstitutionCyclesResponse>(`/billing/reports/institutions/${institutionId}/cycles${searchParams.toString() ? `?${searchParams.toString()}` : ''}`)
    return response.data
  },

  async getBillingReportsMedics(params: {
    from_date?: string
    to_date?: string
    institution_id?: string
    doctor_id?: string
    status?: string
    segment?: string
  } = {}): Promise<BillingReportDoctorResponse> {
    const searchParams = new URLSearchParams()
    if (params.from_date) searchParams.append('from_date', params.from_date)
    if (params.to_date) searchParams.append('to_date', params.to_date)
    if (params.institution_id) searchParams.append('institution_id', params.institution_id)
    if (params.doctor_id) searchParams.append('doctor_id', params.doctor_id)
    if (params.status) searchParams.append('status', params.status)
    if (params.segment) searchParams.append('segment', params.segment)

    const response = await apiClient.get<BillingReportDoctorResponse>(`/billing/reports/medics${searchParams.toString() ? `?${searchParams.toString()}` : ''}`)
    return response.data
  },

  async getBillingMedicEntry(entryId: string): Promise<BillingReportDoctorEntryDetail> {
    const response = await apiClient.get<BillingReportDoctorEntryDetail>(`/billing/reports/medics/${entryId}`)
    return response.data
  },

  // ===========================================================================
  // SEGMENT REPORTS (AFFIDEA) - PER SEGMENT BILLING
  // ===========================================================================

  async getInstitutionSegmentReport(params: {
    institution_id: string
    from_date?: string
    to_date?: string
    month?: number
    year?: number
    status_filter?: string
  }): Promise<BillingReportSegmentInstitutionResponse> {
    const searchParams = new URLSearchParams()
    searchParams.append('institution_id', params.institution_id)
    if (params.from_date) searchParams.append('from_date', params.from_date)
    if (params.to_date) searchParams.append('to_date', params.to_date)
    if (params.month) searchParams.append('month', params.month.toString())
    if (params.year) searchParams.append('year', params.year.toString())
    if (params.status_filter) searchParams.append('status_filter', params.status_filter)

    const response = await apiClient.get<BillingReportSegmentInstitutionResponse>(
      `/billing/reports/institutions/segments?${searchParams.toString()}`
    )
    return response.data
  },

  async getDoctorSegmentReport(params: {
    doctor_id: string
    institution_id: string
    from_date?: string
    to_date?: string
    month?: number
    year?: number
    status_filter?: string
  }): Promise<BillingReportSegmentDoctorResponse> {
    const searchParams = new URLSearchParams()
    searchParams.append('doctor_id', params.doctor_id)
    searchParams.append('institution_id', params.institution_id)
    if (params.from_date) searchParams.append('from_date', params.from_date)
    if (params.to_date) searchParams.append('to_date', params.to_date)
    if (params.month) searchParams.append('month', params.month.toString())
    if (params.year) searchParams.append('year', params.year.toString())
    if (params.status_filter) searchParams.append('status_filter', params.status_filter)

    const response = await apiClient.get<BillingReportSegmentDoctorResponse>(
      `/billing/reports/doctors/segments?${searchParams.toString()}`
    )
    return response.data
  },

  async exportSegmentReport(params: {
    institution_id: string
    format: 'pdf' | 'excel'
    from_date?: string
    to_date?: string
    month?: number
    year?: number
    status_filter?: string
  }): Promise<Blob> {
    const searchParams = new URLSearchParams()
    searchParams.append('institution_id', params.institution_id)
    searchParams.append('format', params.format)
    if (params.from_date) searchParams.append('from_date', params.from_date)
    if (params.to_date) searchParams.append('to_date', params.to_date)
    if (params.month) searchParams.append('month', params.month.toString())
    if (params.year) searchParams.append('year', params.year.toString())
    if (params.status_filter) searchParams.append('status_filter', params.status_filter)

    const response = await apiClient.get(
      `/billing/reports/institutions/segments/export?${searchParams.toString()}`,
      { responseType: 'blob' }
    )
    return response.data
  },

  async exportDoctorSegmentReport(params: {
    doctor_id: string
    institution_id: string
    format: 'pdf' | 'excel' | 'csv'
    from_date?: string
    to_date?: string
    month?: number
    year?: number
    status_filter?: string
  }): Promise<Blob> {
    const searchParams = new URLSearchParams()
    searchParams.append('doctor_id', params.doctor_id)
    searchParams.append('institution_id', params.institution_id)
    searchParams.append('format', params.format)
    if (params.from_date) searchParams.append('from_date', params.from_date)
    if (params.to_date) searchParams.append('to_date', params.to_date)
    if (params.month) searchParams.append('month', params.month.toString())
    if (params.year) searchParams.append('year', params.year.toString())
    if (params.status_filter) searchParams.append('status_filter', params.status_filter)

    const response = await apiClient.get(
      `/billing/reports/doctors/segments/export?${searchParams.toString()}`,
      { responseType: 'blob' }
    )
    return response.data
  },
}

// =============================================================================
// GUARD REPORTS API (Shift-based Reports)
// =============================================================================

export const guardReportsApi = {
  async getInstitutionStandardReport(request: GuardReportInstitutionRequest): Promise<GuardReportStandardResponse> {
    const response = await apiClient.post<GuardReportStandardResponse>(
      '/billing/guard-reports/institution-standard',
      request
    )
    return response.data
  },

  async getInstitutionDetailedReport(request: GuardReportInstitutionRequest): Promise<GuardReportDetailedResponse> {
    const response = await apiClient.post<GuardReportDetailedResponse>(
      '/billing/guard-reports/institution-detailed',
      request
    )
    return response.data
  },

  async getDoctorReport(request: GuardReportDoctorRequest): Promise<GuardReportDoctorResponse> {
    const response = await apiClient.post<GuardReportDoctorResponse>(
      '/billing/guard-reports/doctor',
      request
    )
    return response.data
  },
}